import { useMemo, useState } from 'react';
import type { Behavior, FieldContent, JobTypeVersionContentV3, Stage } from '@capture/shared';
import { Drawer } from '@/components/Drawer';
import { defaultBehaviorForKind } from '../form-config/stages/behavior-picker';
import { kindForEffect, stagesForKind } from './effect-kind';
import { PopulateBody } from '../form-config/behaviors/bodies/PopulateBody';
import { TransformBody } from '../form-config/behaviors/bodies/TransformBody';
import { ComputeBody } from '../form-config/behaviors/bodies/ComputeBody';
import { SubstituteBody } from '../form-config/behaviors/bodies/SubstituteBody';
import { ParseBody } from '../form-config/behaviors/bodies/ParseBody';
import { WebhookBody } from '../form-config/behaviors/bodies/WebhookBody';
import './automations.css';

interface EditInitial {
  formId: string;
  fieldName: string;
  stage: Stage;
  behavior: Behavior;
}

interface Props {
  effectKey: string;
  contentV3: JobTypeVersionContentV3 | null;
  onSave: (formId: string, fieldName: string, stage: Stage, behavior: Behavior) => void;
  onClose: () => void;
  /** Present in edit mode — preloads and locks form/field/stage. */
  initial?: EditInitial;
  /** New-mode hand-off preset — seeds (but does NOT lock) the form + field so
   *  the drawer opens on the field the operator jumped from instead of the
   *  first field. Ignored when `initial` (edit mode) is present. */
  preset?: { formId: string; fieldName: string };
  /** Present in edit mode — shows a Delete button. */
  onDelete?: () => void;
}

function renderBody(
  behavior: Behavior,
  onChange: (b: Behavior) => void,
  fieldNames: string[],
  fields: FieldContent[],
) {
  const shared = { onChange, fieldNames, advanced: true };
  switch (behavior.kind) {
    case 'populate': return <PopulateBody behavior={behavior} {...shared} />;
    case 'transform': return <TransformBody behavior={behavior} {...shared} />;
    case 'compute': return <ComputeBody behavior={behavior} {...shared} />;
    case 'substitute': return <SubstituteBody behavior={behavior} {...shared} />;
    case 'parse': return <ParseBody behavior={behavior} {...shared} />;
    case 'webhook': return <WebhookBody behavior={behavior} fields={fields} {...shared} />;
    default: return null;
  }
}

export function AutomationEditorDrawer({ effectKey, contentV3, onSave, onClose, initial, preset, onDelete }: Props) {
  const kind = kindForEffect(effectKey);
  const forms = contentV3?.forms ?? [];
  const validStages = useMemo(() => (kind ? stagesForKind(kind) : []), [kind]);
  const isEdit = initial !== undefined;

  // Edit locks form/field; a new-mode hand-off preset only SEEDS them (operator
  // can still change the field). `initial` (edit) and `preset` (new) are never
  // both set, and `initial?.` takes precedence in each useState below, so the
  // preset can't override edit mode. A preset field absent on the form falls
  // back to the first field.
  const [formId, setFormId] = useState<string>(initial?.formId ?? preset?.formId ?? forms[0]?.id ?? '');
  const selectedForm = forms.find((f) => f.id === formId) ?? forms[0];
  const fields = selectedForm?.fields ?? [];

  const presetFieldName =
    preset && fields.some((f) => f.name === preset.fieldName) ? preset.fieldName : undefined;
  const [fieldName, setFieldName] = useState<string>(
    initial?.fieldName ?? presetFieldName ?? fields[0]?.name ?? '',
  );
  const [stage, setStage] = useState<Stage>(initial?.stage ?? validStages[0] ?? ('pre-export' as Stage));
  const [behavior, setBehavior] = useState<Behavior>(() =>
    initial?.behavior ?? (kind ? defaultBehaviorForKind(kind) : ({ kind: 'transform', op: 'uppercase', params: {} } as Behavior)),
  );

  if (!kind) return null;

  const fieldNames = fields.map((f) => f.name);
  const title = `${isEdit ? 'Edit' : 'New'} automation — ${effectKey.replace(/_/g, ' ')}`;

  function handleFormChange(nextFormId: string) {
    setFormId(nextFormId);
    const nextFields = forms.find((f) => f.id === nextFormId)?.fields ?? [];
    setFieldName(nextFields[0]?.name ?? '');
  }

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={title}
      width="md"
      footer={
        <>
          {onDelete && (
            <button className="btn btn--danger" data-testid="automation-delete-btn" style={{ marginRight: 'auto' }} onClick={onDelete}>
              Delete
            </button>
          )}
          <button className="btn" onClick={onClose} data-testid="automation-cancel-btn">Cancel</button>
          <button
            className="btn btn--primary"
            data-testid="automation-save-btn"
            disabled={!formId || !fieldName}
            onClick={() => onSave(formId, fieldName, stage, behavior)}
          >
            Save
          </button>
        </>
      }
    >
      <div className="automation-editor" data-testid="automation-editor">
        <label className="automation-editor__row">
          <span>Form</span>
          <select data-testid="automation-form-select" value={formId} disabled={isEdit} onChange={(e) => handleFormChange(e.target.value)}>
            {forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </label>

        <label className="automation-editor__row">
          <span>Field</span>
          <select data-testid="automation-field-select" value={fieldName} disabled={isEdit} onChange={(e) => setFieldName(e.target.value)}>
            {fields.map((f) => <option key={f.name} value={f.name}>{f.label || f.name}</option>)}
          </select>
        </label>

        <label className="automation-editor__row">
          <span>When</span>
          <select data-testid="automation-stage-select" value={stage} disabled={isEdit} onChange={(e) => setStage(e.target.value as Stage)}>
            {validStages.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>

        <div className="automation-editor__body">
          {renderBody(behavior, setBehavior, fieldNames, fields)}
        </div>
      </div>
    </Drawer>
  );
}
